import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Clock, CircleAlert, Plus, TriangleAlert, MapPinOff, CalendarRange, Play,
} from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { EmptyState } from "@pessoas/ui";
import { trpc } from "../lib/trpc.js";
import { horas, relogio, hora, diaDaSemana, ehFimDeSemana, primeiroEUltimoDoMes } from "../lib/tempo.js";
import { data as fData } from "../lib/formato.js";
import { usePode } from "../shell/Sessao.js";

const competenciaDeHoje = () => new Date().toISOString().slice(0, 7);

/**
 * Espelho do mês.
 *
 * Lê a APURAÇÃO, uma linha por dia já totalizada, e traz as marcações por
 * trás de cada número. Varrer marcação para montar a tela faria os 31 dias
 * pagarem o custo do pareamento a cada abertura.
 */
export function Espelho() {
  const [competencia, setCompetencia] = useState(competenciaDeHoje());
  const [vinculoId, setVinculoId] = useState<string | null>(null);
  const [ajustando, setAjustando] = useState<{ dia: string } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const podeTratar = usePode("ponto.tratar");
  const qc = useQueryClient();
  const { de, ate } = primeiroEUltimoDoMes(competencia);

  const pessoas = useQuery({
    queryKey: ["cadastro", "pessoas", "", false],
    queryFn: () => trpc.cadastro.pessoas.query({ busca: "", incluirDesligados: false, limite: 500 }),
  });
  const comVinculo = (pessoas.data ?? []).filter((p) => p.vinculoId);
  const alvo = vinculoId ?? comVinculo[0]?.vinculoId ?? null;

  const escalas = useQuery({
    queryKey: ["ponto", "escalas"],
    queryFn: () => trpc.jornada.escalas.query(),
    enabled: podeTratar,
  });

  const materializar = useMutation({
    mutationFn: (escalaId: string) => trpc.jornada.materializar.mutate({
      vinculoId: alvo!, escalaId, de, ate,
    }),
    onSuccess: (r) => {
      setAviso(`Calendário gerado para ${r.gravados} dias.`
        + (r.preservados > 0 ? ` ${r.preservados} dias manuais foram preservados.` : ""));
      void qc.invalidateQueries({ queryKey: ["ponto"] });
    },
    onError: (e: unknown) => setAviso(e instanceof Error ? e.message : String(e)),
  });

  const apurar = useMutation({
    mutationFn: () => trpc.jornada.apurar.mutate({ vinculoId: alvo!, de, ate }),
    onSuccess: (r) => {
      // `puladas` é o `inputHash` funcionando: o dia que não mudou encerra sem
      // escrever nada, e é isso que torna viável reapurar 90 dias de 500
      // pessoas num job noturno.
      setAviso(`${r.apuradas} dias apurados, ${r.puladas} sem mudança de entrada.`);
      void qc.invalidateQueries({ queryKey: ["ponto"] });
    },
    onError: (e: unknown) => setAviso(e instanceof Error ? e.message : String(e)),
  });

  const criarPadrao = useMutation({
    mutationFn: () => trpc.jornada.criarEscala.mutate({
      nome: "Semana de 44 horas", padrao: "semana44", entrada: "08:00",
    }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ponto", "escalas"] }),
  });

  const espelho = useQuery({
    queryKey: ["ponto", "espelho", alvo, de, ate],
    queryFn: () => trpc.jornada.espelho.query({ vinculoId: alvo!, de, ate }),
    enabled: !!alvo,
  });

  if (pessoas.isPending) {
    return <Pagina secao="Jornada" titulo="Espelho do mês"><Carregando linhas={6} /></Pagina>;
  }
  if (comVinculo.length === 0) {
    return (
      <Pagina secao="Jornada" titulo="Espelho do mês">
        <EmptyState
          titulo="Ninguém tem vínculo aberto"
          descricao="O espelho é por vínculo: sem admissão não há jornada prevista para comparar."
        />
      </Pagina>
    );
  }

  const dias = espelho.data?.dias ?? [];
  const total = dias.reduce((s, d) => ({
    previstos: s.previstos + d.minutosPrevistos,
    trabalhados: s.trabalhados + d.minutosTrabalhados,
    faltantes: s.faltantes + d.minutosFaltantes,
    noturnos: s.noturnos + d.minutosNoturnos,
    extras: s.extras + Object.values(d.minutosExtras ?? {}).reduce((a, b) => a + Number(b), 0),
  }), { previstos: 0, trabalhados: 0, faltantes: 0, noturnos: 0, extras: 0 });

  return (
    <Pagina
      secao="Jornada" titulo="Espelho do mês"
      descricao="Uma linha por dia, já totalizada, com as marcações por trás de cada número."
      acoes={
        <div className="flex items-center gap-2">
          <select className="campo h-8 w-auto text-xs" value={alvo ?? ""}
                  aria-label="Pessoa"
                  onChange={(e) => setVinculoId(e.target.value)}>
            {comVinculo.map((p) => (
              <option key={p.vinculoId!} value={p.vinculoId!}>{p.nome}</option>
            ))}
          </select>
          <input type="month" className="campo h-8 w-auto text-xs" value={competencia}
                 aria-label="Competência"
                 onChange={(e) => setCompetencia(e.target.value)} />
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Resumo rotulo="Previsto" valor={horas(total.previstos)} />
        <Resumo rotulo="Trabalhado" valor={horas(total.trabalhados)} />
        <Resumo rotulo="Horas extras" valor={horas(total.extras)}
                sinal={total.extras > 0 ? "atencao" : undefined} />
        <Resumo rotulo="Faltantes" valor={horas(total.faltantes)}
                sinal={total.faltantes > 0 ? "critico" : undefined} />
      </div>

      {podeTratar && (
        <div className="flex flex-wrap items-center gap-2">
          {(escalas.data?.length ?? 0) === 0 ? (
            <button className="botao" disabled={criarPadrao.isPending}
                    onClick={() => criarPadrao.mutate()}>
              <CalendarRange aria-hidden size={15} />
              {criarPadrao.isPending ? "Criando" : "Criar a escala padrão"}
            </button>
          ) : (
            <>
              <select className="campo h-8 w-auto text-xs" aria-label="Escala"
                      defaultValue={escalas.data![0]!.id}
                      onChange={(e) => materializar.mutate(e.target.value)}>
                {escalas.data!.map((e) => (
                  <option key={e.id} value={e.id}>{e.nome}</option>
                ))}
              </select>
              <button className="botao" disabled={materializar.isPending}
                      onClick={() => materializar.mutate(escalas.data![0]!.id)}>
                <CalendarRange aria-hidden size={15} />
                {materializar.isPending ? "Gerando" : "Gerar o calendário"}
              </button>
            </>
          )}
          <button className="botao" data-variante="primario" disabled={apurar.isPending}
                  onClick={() => { setAviso(null); apurar.mutate(); }}>
            <Play aria-hidden size={15} />
            {apurar.isPending ? "Apurando" : "Apurar o período"}
          </button>
        </div>
      )}

      {aviso && (
        <p className="eop-erro" role="status">
          <CircleAlert aria-hidden size={15} /><span>{aviso}</span>
        </p>
      )}

      {espelho.isPending && <Carregando linhas={8} />}
      {espelho.isError && <Falha erro={espelho.error} tentarDeNovo={() => void espelho.refetch()} />}

      {espelho.isSuccess && (espelho.data.naoApurados?.length ?? 0) > 0 && (
        <Cartao titulo="Dias com batida e sem apuração"
                subtitulo="Existe marcação que ninguém apurou, e ela sumiria da tela se o espelho só lesse a apuração">
          <ul className="eop-agenda">
            {espelho.data.naoApurados.map((d) => (
              <li key={d}>
                <span className="num eop-agenda-dia">{fData(d).slice(0, 5)}</span>
                <span className="min-w-0 flex-1">
                  <span className="eop-agenda-nome">{diaDaSemana(d)}, não apurado</span>
                </span>
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {espelho.isSuccess && dias.length === 0 && (
        <EmptyState
          titulo="Nada apurado nesta competência"
          descricao="A apuração roda sobre as marcações e a escala prevista. Sem escala, não há previsto para comparar."
        />
      )}

      {espelho.isSuccess && dias.length > 0 && (
        <Cartao titulo={`Dias de ${fData(de).slice(3)}`}
                subtitulo="Clique num dia para ver as marcações que o geraram">
          <div className="rolagem-tabela">
            <table className="eop-espelho">
              <thead>
                <tr>
                  <th>Dia</th><th>Marcações</th>
                  <th className="num">Previsto</th><th className="num">Trabalhado</th>
                  <th className="num">Extras</th><th className="num">Faltantes</th>
                  <th>Sinal</th>
                  {podeTratar && <th aria-label="Ações" />}
                </tr>
              </thead>
              <tbody>
                {dias.map((d) => {
                  const extras = Object.values(d.minutosExtras ?? {})
                    .reduce((a, b) => a + Number(b), 0);
                  return (
                    <tr key={d.dataRef} data-fds={ehFimDeSemana(d.dataRef) || undefined}>
                      <td>
                        <span className="num">{d.dataRef.slice(8)}</span>
                        <span className="eop-espelho-dow">{diaDaSemana(d.dataRef)}</span>
                      </td>
                      <td>
                        {d.marcacoes.length === 0
                          ? <span style={{ color: "var(--faint-foreground)" }}>sem batida</span>
                          : (
                            <span className="eop-batidas">
                              {d.marcacoes.map((m) => (
                                <span key={m.id} className="num eop-batida"
                                      data-tratada={!!m.corrigeId || undefined}
                                      title={m.corrigeId ? `tratamento: ${m.motivoTratamento}` : undefined}>
                                  {hora(m.dataHoraLocal)}
                                  {m.relogioSuspeito && (
                                    <TriangleAlert aria-label="relógio suspeito" size={10} />
                                  )}
                                  {m.dentroCerca === false && (
                                    <MapPinOff aria-label="fora da cerca" size={10} />
                                  )}
                                </span>
                              ))}
                            </span>
                          )}
                      </td>
                      <td className="num">{relogio(d.minutosPrevistos)}</td>
                      <td className="num">{relogio(d.minutosTrabalhados)}</td>
                      <td className="num" data-forte={extras > 0 || undefined}>
                        {extras > 0 ? relogio(extras) : ""}
                      </td>
                      <td className="num" data-alerta={d.minutosFaltantes > 0 || undefined}>
                        {d.minutosFaltantes > 0 ? relogio(d.minutosFaltantes) : ""}
                      </td>
                      <td>
                        {/* Marcação ímpar, interjornada violada e intrajornada
                            suprimida são o que o fiscal procura primeiro. */}
                        {d.marcacoes.length % 2 === 1 && (
                          <span className="pilula" title="número ímpar de batidas">ímpar</span>
                        )}
                        {d.interjornadaViolada && (
                          <span className="pilula" title="menos de 11 horas desde a saída anterior">
                            interjornada
                          </span>
                        )}
                        {d.minutosIntrajornadaSuprimida > 0 && (
                          <span className="pilula" title="intervalo abaixo do previsto">intervalo</span>
                        )}
                      </td>
                      {podeTratar && (
                        <td>
                          <button className="botao-icone" aria-label={`Ajustar ${d.dataRef}`}
                                  onClick={() => setAjustando({ dia: d.dataRef })}>
                            <Plus aria-hidden size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Cartao>
      )}

      {ajustando && alvo && (
        <FormAjuste
          vinculoId={alvo} dia={ajustando.dia}
          aoFechar={() => setAjustando(null)}
          aoGravar={() => {
            setAjustando(null);
            void qc.invalidateQueries({ queryKey: ["ponto"] });
          }}
        />
      )}
    </Pagina>
  );
}

function Resumo({ rotulo, valor, sinal }:
  { rotulo: string; valor: string; sinal?: "atencao" | "critico" | undefined }) {
  return (
    <div className="painel indicador p-4 pl-5">
      <div className="flex items-center gap-1.5">
        {sinal && <span aria-hidden className={`sinal sinal-${sinal}`} />}
        <span className="eyebrow">{rotulo}</span>
      </div>
      <p className="num mt-2.5 text-[1.75rem] font-semibold leading-none">{valor}</p>
    </div>
  );
}

/**
 * Inclusão de marcação pelo tratamento.
 *
 * Não edita nada: grava marcação NOVA. Se o sistema nascer com
 * `UPDATE marcacao SET hora = ...`, ele não vira REP-P nunca.
 */
function FormAjuste(
  { vinculoId, dia, aoFechar, aoGravar }:
  { vinculoId: string; dia: string; aoFechar: () => void; aoGravar: () => void },
) {
  const [hhmm, setHhmm] = useState("");
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const gravar = useMutation({
    mutationFn: () => trpc.jornada.marcar.mutate({
      vinculoId, dataHoraLocal: `${dia}T${hhmm}:00`, origem: "web",
    }),
    onSuccess: aoGravar,
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : String(e)),
  });

  return (
    <div className="eop-modal-fundo" role="dialog" aria-modal="true"
         aria-label="Incluir marcação"
         onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="eop-modal" style={{ maxWidth: "28rem" }}>
        <header className="painel-topo">
          <h2 className="text-[0.9375rem] font-semibold">Incluir marcação em {fData(dia)}</h2>
          <p className="mt-1 text-xs" style={{ color: "var(--faint-foreground)" }}>
            A marcação original nunca é apagada nem alterada. Esta entra como
            registro novo, e as duas ficam no espelho.
          </p>
        </header>
        <form className="painel-corpo space-y-3.5"
              onSubmit={(e) => { e.preventDefault(); setErro(null); gravar.mutate(); }}>
          <label className="block">
            <span className="eop-rotulo">Horário</span>
            <input className="campo mt-1.5 num" type="time" required value={hhmm}
                   onChange={(e) => setHhmm(e.target.value)} />
          </label>
          <label className="block">
            <span className="eop-rotulo">Motivo</span>
            <input className="campo mt-1.5" required minLength={5} value={motivo}
                   placeholder="Esqueceu de bater na saída"
                   onChange={(e) => setMotivo(e.target.value)} />
          </label>
          {erro && (
            <p className="eop-erro" role="alert">
              <CircleAlert aria-hidden size={15} /><span>{erro}</span>
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="botao" data-variante="silencioso" onClick={aoFechar}>
              Cancelar
            </button>
            <button type="submit" className="botao" data-variante="primario"
                    disabled={gravar.isPending || !hhmm || motivo.length < 5}>
              <Clock aria-hidden size={15} />
              {gravar.isPending ? "Gravando" : "Incluir"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
